import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormRow } from '@tanium/react-form';
import get from 'lodash.get';
import { Field, FieldProps, FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@tanium/react-theme-context';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import Tooltip from 'components/tooltip/Tooltip';
import { TARGET_ACCESS_ID } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { HostTargetAccess } from 'gql/HostTargetAccess';

interface HostBasedAccessProps {
  hostTargetAccess: HostTargetAccess[];
}

export const HostBasedAccess = ({ hostTargetAccess }: HostBasedAccessProps): React.ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();

  const formInputIdPrefix = 'create-request-';

  const options = hostTargetAccess.map((access) => {
    return {
      label: `${access.userName}@${access.hostName}`,
      value: access.id,
      description: access.description,
    };
  });

  return (
    <Box display="inline-flex" mr={1}>
      <Field name={TARGET_ACCESS_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('HOST')}
            required
            description={
              <Box>
                <Box>{t('HOST_DESCRIPTION')}</Box>
              </Box>
            }
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
            data-testid={`${formInputIdPrefix}${field.name}`}
          >
            <FormSelectInput
              onChange={(option): void => {
                void form.setFieldValue(field.name, option.value);
              }}
              value={options?.find((option) => option.value === field.value)}
              inputId={`${formInputIdPrefix}${field.name}`}
              options={options}
              aria-label={t('HOST')}
              formatOptionLabel={(
                option: {
                  value: string;
                  label: string;
                  description: string;
                },
                state
              ) => {
                const isSelected =
                  state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                /* istanbul ignore next */
                const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                return (
                  <Stack direction="column" itemSpacing={0.5}>
                    <Box>{option.label}</Box>
                    <Tooltip content={option.description}>
                      <Box color={subtextColor} fontSize={11}>
                        {option.description}
                      </Box>
                    </Tooltip>
                  </Stack>
                );
              }}
              styles={{
                singleValue: (base) => ({
                  ...base,
                  marginTop: theme.space[1],
                  marginBottom: theme.space[1],
                  position: 'relative',
                  transform: 'none',
                  whiteSpace: 'normal',
                  maxHeight: '3.75em',
                }),
                input: (base) => ({
                  ...base,
                  position: 'absolute',
                  top: theme.space[1] - 2,
                  paddingTop: 0,
                }),
              }}
            />
          </FormRow>
        )}
      </Field>
    </Box>
  );
};

export default HostBasedAccess;
